import { FormControl, FormControlLabel, Radio, RadioGroup } from '@material-ui/core'
import { VisualArticle } from 'components/domain/stockPricing/StockDisplay/VisualArticle'
import { Button } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import { editUserAttribute } from 'lib/api'
import { useInventoryStore } from 'lib/hooks/useInventoryStore'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CardLanguage,
  CountryEdition,
  FINISH_TYPE_LABELS,
  InventoryArticle,
  TAXATION_ICON_VISIBILITY,
  TAXATION_SCHEME,
  TAXATION_SCHEME_CONVERSIONS,
} from 'shared'
import { MARGIN_TAXATION_ICON, STANDARD_TAXATION_ICON } from 'utils/constants'

export const TaxationSchemeSettings = (): JSX.Element => {
  const { t } = useTranslation()
  const { user, setUser } = useUser()
  const { enqueueSnackbar } = useSnackbar()
  const [singles, setSingles] = useState<TAXATION_SCHEME>(user.defaultTaxationSchema?.singles)
  const [sealed, setSealed] = useState<TAXATION_SCHEME>(user.defaultTaxationSchema?.sealed)
  const [disableMargin] = useState(!user.enableMarginTaxation)
  const [iconVisibility, setIconVisibility] = useState(
    user.defaultTaxationSchema.taxationIconVisibility
  )

  const { setDisplayTaxationIcon, setDefaultTaxationScheme } = useInventoryStore()

  useEffect(() => {
    setDisplayTaxationIcon(iconVisibility)
  }, [iconVisibility, setDisplayTaxationIcon])

  useEffect(() => {
    setDefaultTaxationScheme(singles)
  }, [singles, setDefaultTaxationScheme])

  const previewStandardArticle = useMemo(
    () =>
      new InventoryArticle({
        idArticle: 'preview-standard',
        price: 5,
        quantity: 1,
        condition: 'NM',
        idLanguage: CardLanguage.English,
        isPlayset: false,
        isSigned: false,
        isFirstEd: false,
        finishType: FINISH_TYPE_LABELS.REGULAR,
        countryEdition: CountryEdition.Regular,
        standardTaxationQuantity: 1,
        marginTaxationQuantity: 0,
        card: {
          _id: 999999,
          cn: '001',
          rarity: 1,
          set: 'SET',
          expansion: 'Example Expansion',
          name: 'Standard Article',
          categoryName: 'Magic Singles',
          locNames: { DE: '', ES: '', FR: '', IT: '' },
        },
        ptArticleReservedQuantity: 0,
      }),
    []
  )

  const previewMarginArticle = useMemo(
    () =>
      new InventoryArticle({
        idArticle: 'preview-margin',
        price: 12,
        quantity: 1,
        condition: 'NM',
        idLanguage: CardLanguage.English,
        isPlayset: false,
        isSigned: false,
        isFirstEd: false,
        finishType: FINISH_TYPE_LABELS.REGULAR,
        countryEdition: CountryEdition.Regular,
        standardTaxationQuantity: 0,
        marginTaxationQuantity: 1,
        card: {
          _id: 999999,
          cn: '002',
          rarity: 1,
          set: 'SET',
          expansion: 'Example Expansion',
          name: 'Margin Article',
          categoryName: 'Magic Singles',
          locNames: { DE: '', ES: '', FR: '', IT: '' },
        },
        ptArticleReservedQuantity: 0,
      }),
    []
  )

  const previewMixedArticle = useMemo(
    () =>
      new InventoryArticle({
        idArticle: 'preview-mixed',
        price: 8,
        quantity: 3,
        condition: 'NM',
        idLanguage: CardLanguage.English,
        isPlayset: false,
        isSigned: false,
        isFirstEd: false,
        finishType: FINISH_TYPE_LABELS.REGULAR,
        countryEdition: CountryEdition.Regular,
        standardTaxationQuantity:
          iconVisibility === TAXATION_ICON_VISIBILITY.ALL || singles !== TAXATION_SCHEME.STANDARD
            ? 2
            : 0,
        marginTaxationQuantity:
          iconVisibility === TAXATION_ICON_VISIBILITY.ALL || singles !== TAXATION_SCHEME.MARGIN
            ? 1
            : 0,
        card: {
          _id: 999999,
          cn: '003',
          rarity: 1,
          set: 'SET',
          expansion: 'Example Expansion',
          name: 'Mixed Article',
          categoryName: 'Magic Singles',
          locNames: { DE: '', ES: '', FR: '', IT: '' },
        },
        ptArticleReservedQuantity: 0,
      }),
    [iconVisibility, singles]
  )

  const nothingChanged =
    singles === user.defaultTaxationSchema?.singles && sealed === user.defaultTaxationSchema?.sealed

  const handleCheckSingles = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSingles(Number(event.target.value) as TAXATION_SCHEME)
  }

  const handleCheckSealed = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSealed(Number(event.target.value) as TAXATION_SCHEME)
  }

  const handleCheckIcon = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIconVisibility(event.target.value)
  }

  const handleSaveSettings = async () => {
    try {
      const response = await editUserAttribute(
        {
          singles: TAXATION_SCHEME_CONVERSIONS.find((entry) => entry.code === singles)?.code,
          sealed: TAXATION_SCHEME_CONVERSIONS.find((entry) => entry.code === sealed)?.code,
          taxationIconVisibility: iconVisibility,
        },
        'defaultTaxationSchema'
      )

      if (response) {
        enqueueSnackbar(t('taxation.success.settingsSaved'), { variant: 'success' })
        setUser({ ...user, ...response })
      }
    } catch (e) {
      enqueueSnackbar(t('taxation.errors.errorSaveSettings'), { variant: 'error' })
    }
  }

  return (
    <div>
      <h2>{t('taxation.settings.defaultInfo')}</h2>
      <div className="mt-3">
        <p className="fs-6">{t('taxation.settings.defaultInfoSubtitle')}</p>
      </div>

      <h6 className="m-0 p-0 mt-4 fs-6 mb-2">{t('taxation.settings.selectDefaults')}</h6>
      <span className="fs-6">{t('taxation.settings.selectDefaultsSubtitle')}</span>

      <div className="d-flex flex-column me-5 mt-3">
        <span className="fs-6">{t('ptInventory.stockReview.singles')}: </span>
        <FormControl>
          <RadioGroup value={singles} onChange={handleCheckSingles}>
            <FormControl>
              <RadioGroup value={singles} onChange={handleCheckSingles}>
                <FormControlLabel
                  value={TAXATION_SCHEME.STANDARD}
                  control={<Radio />}
                  className="fw-light fs-5"
                  label={
                    <span>
                      {t('taxation.settings.allItemsToStandard')}{' '}
                      <img src={STANDARD_TAXATION_ICON} alt="standard_taxation_icon" />
                    </span>
                  }
                  data-testid="check-standard"
                />
                <FormControlLabel
                  value={TAXATION_SCHEME.MARGIN}
                  control={<Radio />}
                  className="fw-light fs-5"
                  label={
                    <span>
                      {t('taxation.settings.allItemsToMargin')}{' '}
                      <img src={MARGIN_TAXATION_ICON} alt="margin_taxation_icon" />
                    </span>
                  }
                  data-testid="check-margin"
                  disabled={disableMargin}
                />
              </RadioGroup>
            </FormControl>
          </RadioGroup>
        </FormControl>
      </div>

      <div className="d-flex flex-column mt-4">
        <span className="fs-6">{t('ptInventory.stockReview.accessories')}: </span>
        <FormControl>
          <RadioGroup value={sealed} onChange={handleCheckSealed}>
            <FormControl>
              <RadioGroup value={sealed} onChange={handleCheckSealed}>
                <FormControlLabel
                  value={TAXATION_SCHEME.STANDARD}
                  control={<Radio />}
                  className="fw-light fs-5"
                  label={
                    <span>
                      {t('taxation.settings.allItemsToStandard')}{' '}
                      <img src={STANDARD_TAXATION_ICON} alt="standard_taxation_icon" />
                    </span>
                  }
                  data-testid="check-vat"
                />
                <FormControlLabel
                  value={TAXATION_SCHEME.MARGIN}
                  control={<Radio />}
                  className="fw-light fs-5"
                  label={
                    <span>
                      {t('taxation.settings.allItemsToMargin')}{' '}
                      <img src={MARGIN_TAXATION_ICON} alt="margin_taxation_icon" />
                    </span>
                  }
                  data-testid="check-margin"
                  disabled={disableMargin}
                />
              </RadioGroup>
            </FormControl>
          </RadioGroup>
        </FormControl>
      </div>

      <div className="mt-3 d-flex flex-row align-items-center">
        <Button
          disabled={nothingChanged}
          onClick={() => {
            handleSaveSettings()
          }}
          variant="contained"
        >
          {t('taxation.settings.saveSchemeSettings')}
        </Button>
      </div>

      <div className="d-flex flex-column mt-5">
        <strong className="fs-6 mb-2">{t('taxation.settings.taxationIconTitle')}</strong>
        <div className="d-flex flex-row align-items-start gap-5">
          <div>
            <FormControl>
              <RadioGroup value={iconVisibility} onChange={handleCheckIcon}>
                <FormControl>
                  <RadioGroup value={iconVisibility} onChange={handleCheckIcon}>
                    <FormControlLabel
                      value={TAXATION_ICON_VISIBILITY.ONLY_DIFFERENT}
                      control={<Radio />}
                      className="fw-light fs-5"
                      label={t('taxation.settings.showOnlyDifferent')}
                      data-testid="check-only-different"
                    />
                    <FormControlLabel
                      value={TAXATION_ICON_VISIBILITY.ALL}
                      control={<Radio />}
                      className="fw-light fs-5"
                      label={t('taxation.settings.showAll')}
                      data-testid="check-all"
                    />
                  </RadioGroup>
                </FormControl>
              </RadioGroup>
            </FormControl>

            <div className="mt-4">
              <strong className="fs-6 mb-2 d-block">
                {t('taxation.settings.mixedArticlesTitle')}{' '}
                <img src={STANDARD_TAXATION_ICON} alt="standard_taxation_icon" /> /{' '}
                <img src={MARGIN_TAXATION_ICON} alt="margin_taxation_icon" />
              </strong>
              <p className="fs-6">{t('taxation.settings.mixedArticlesDescription')}</p>
            </div>

            <div className="mt-3">
              <Button
                disabled={iconVisibility === user.defaultTaxationSchema?.taxationIconVisibility}
                onClick={handleSaveSettings}
                variant="contained"
              >
                {t('taxation.settings.saveIconSettings')}
              </Button>
            </div>
          </div>

          <div>
            <span className="fw-bold" style={{ fontSize: 12, color: '#888' }}>
              {t('taxation.settings.preview')}
            </span>
            <div className="d-flex flex-row gap-3 mt-2">
              <VisualArticle article={previewStandardArticle} currency="EUR" displayOnly />
              <VisualArticle article={previewMarginArticle} currency="EUR" displayOnly />
              <VisualArticle article={previewMixedArticle} currency="EUR" displayOnly />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
